import LabOrUser from "../api/models/advanced-search/LabOrUser";
import { TestRequestAdvancedSearchRQ } from "../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import PrioritySearchTerm from "../api/models/test-request/PrioritySearchTerm";
import SearchMode from "../models/SearchMode";
import ViewMode from "../models/ViewMode";
import TestRequestOverviewColumns, {
  configurableColumns,
} from "../pages/Dashboard/TestRequestsTable/TestRequestsOverviewColumns";
import { ADVANCED_SEARCH_INITIAL } from "../pages/Search/AdvancedSearch.utils";

const DEFAULTS = {
  dashboardView: ViewMode.Table,
  descriptionShowPrefilled: false,
  sidenavMinimized: null,
  rowsPerPage: 25,
  selectedColumns: configurableColumns,
  showDashboardKPIs: true,
  searchTermsBasicDashboard: [],
  searchObjectAdvancedDashboard: {
    customer: "",
    products: [],
    purpose: "",
    substrates: [],
    terms: [],
    prioritySearchTerm: "",
    testMethods: [],
  },
  searchObjectAdvancedArchive: {
    customer: "",
    products: [],
    purpose: "",
    substrates: [],
    terms: [],
    prioritySearchTerm: "",
    testMethods: [],
  },
  dashboardSearchMode: SearchMode.Basic,
};

export type SortDirection = "desc" | "asc";

export interface UserSettings {
  dashboardUserOrLab: LabOrUser;
  dashboardSearchMode: SearchMode;
  dashboardView: ViewMode;
  descriptionShowPrefilled: boolean;
  rowsPerPage: number;
  searchObjectAdvancedArchive: TestRequestAdvancedSearchRQ;
  searchObjectAdvancedDashboard: TestRequestAdvancedSearchRQ;
  searchTermsBasicDashboard: string[];
  prioritySearchTermDashboard: PrioritySearchTerm | "";
  selectedColumns: TestRequestOverviewColumns[];
  showDashboardKPIs: boolean;
  sidenavMinimized: boolean | null;
}

export function setUserSettings(userSettings: UserSettings): void {
  localStorage.setItem("userSettings", JSON.stringify(userSettings));
}

export function getUserSettings(): UserSettings {
  const userSettingsFromLocalStorage = localStorage.getItem("userSettings");

  let userSettings;
  if (userSettingsFromLocalStorage) {
    userSettings = JSON.parse(userSettingsFromLocalStorage);
  } else {
    userSettings = DEFAULTS;
  }

  return userSettings;
}

export function getDashboardUserOrLab(): LabOrUser {
  return getUserSettings().dashboardUserOrLab;
}

export function setDashboardUserOrLab(newValue: LabOrUser): void {
  const userSettings = getUserSettings();
  userSettings.dashboardUserOrLab = newValue;
  setUserSettings(userSettings);
}

export function getDashboardSearchMode(): SearchMode {
  return getUserSettings().dashboardSearchMode;
}

export function setDashboardSearchMode(newValue: SearchMode): void {
  const userSettings = getUserSettings();
  userSettings.dashboardSearchMode = newValue;
  setUserSettings(userSettings);
}

export function getDashboardView(): ViewMode {
  return getUserSettings().dashboardView;
}

export function setDashboardView(newValue: ViewMode): void {
  const userSettings = getUserSettings();
  userSettings.dashboardView = newValue;
  setUserSettings(userSettings);
}

export function getDescriptionShowPrefilled() {
  return getUserSettings().descriptionShowPrefilled;
}

export function setDescriptionShowPrefilled(newValue: boolean) {
  const userSettings = getUserSettings();
  userSettings.descriptionShowPrefilled = newValue;
  setUserSettings(userSettings);
}

export function getRowsPerPage(): number {
  return getUserSettings().rowsPerPage;
}

export function setRowsPerPage(newValue: number): void {
  const userSettings = getUserSettings();
  userSettings.rowsPerPage = newValue;
  setUserSettings(userSettings);
}

export function getSearchObjectAdvancedArchive(): TestRequestAdvancedSearchRQ {
  return getUserSettings().searchObjectAdvancedArchive;
}

export function setSearchObjectAdvancedArchive(newValue: TestRequestAdvancedSearchRQ): void {
  const userSettings = getUserSettings();
  userSettings.searchObjectAdvancedArchive = newValue;
  setUserSettings(userSettings);
}

export function resetSearchObjectAdvancedArchive(): void {
  const userSettings = getUserSettings();
  userSettings.searchObjectAdvancedArchive = ADVANCED_SEARCH_INITIAL;
  setUserSettings(userSettings);
}

export function getSearchObjectAdvancedDashboard(): TestRequestAdvancedSearchRQ {
  return getUserSettings().searchObjectAdvancedDashboard;
}

export function setSearchObjectAdvancedDashboard(newValue: TestRequestAdvancedSearchRQ): void {
  const userSettings = getUserSettings();
  userSettings.searchObjectAdvancedDashboard = newValue;
  setUserSettings(userSettings);
}

export function resetSearchObjectAdvancedDashboard(): void {
  const userSettings = getUserSettings();
  userSettings.searchObjectAdvancedDashboard = ADVANCED_SEARCH_INITIAL;
  setUserSettings(userSettings);
}

export function getSearchTermsBasicDashboard(): string[] {
  return getUserSettings().searchTermsBasicDashboard;
}

export function setSearchTermsBasicDashboard(newValueArr: string[]): void {
  const userSettings = getUserSettings();
  userSettings.searchTermsBasicDashboard = newValueArr;
  setUserSettings(userSettings);
}

export function resetSearchTermsBasicDashboard(): void {
  const userSettings = getUserSettings();
  userSettings.searchTermsBasicDashboard = [];
  setUserSettings(userSettings);
}

export function getPrioritySearchTermDashboard(): PrioritySearchTerm {
  return getUserSettings().prioritySearchTermDashboard as PrioritySearchTerm;
}

export function setPrioritySearchTermDashboard(newValueArr: PrioritySearchTerm): void {
  const userSettings = getUserSettings();
  userSettings.prioritySearchTermDashboard = newValueArr;
  setUserSettings(userSettings);
}

export function resetPrioritySearchTermDashboard(): void {
  const userSettings = getUserSettings();
  userSettings.prioritySearchTermDashboard = "";
  setUserSettings(userSettings);
}

export function getSelectedColumns(): TestRequestOverviewColumns[] {
  return getUserSettings().selectedColumns;
}

export function setSelectedColumns(newValue: TestRequestOverviewColumns[]): void {
  const userSettings = getUserSettings();
  userSettings.selectedColumns = newValue;
  setUserSettings(userSettings);
}

export function getShowDashboardKPIs(): boolean {
  return getUserSettings().showDashboardKPIs;
}

export function setShowDashboardKPIs(newValue: boolean): void {
  const userSettings = getUserSettings();
  userSettings.showDashboardKPIs = newValue;
  setUserSettings(userSettings);
}

export function getSidenavMinimized(): boolean | null {
  return getUserSettings().sidenavMinimized;
}

export function setSidenavMinimized(newValue: boolean): void {
  const userSettings = getUserSettings();
  userSettings.sidenavMinimized = newValue;
  setUserSettings(userSettings);
}
